import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import useAxios from '../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../types/pagination'
import { IProduct } from '../../../../types/product'
import ImgTag from '../../../../components/ImgTag'
import type { ISalepageFormProduct } from '../../types'
import Pagination from '../../../../components/Pagination'

interface ProductSelectModalProps {
  open: boolean
  onClose: () => void
  selectedProducts: ISalepageFormProduct[]
  onSelect: (product: IProduct) => void
}

export const ProductSelectModal: React.FC<ProductSelectModalProps> = ({
  open,
  onClose,
  selectedProducts,
  onSelect,
}) => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const perPage = 10

  const selectedIds = new Set(selectedProducts.map((p) => p.objectId))

  useEffect(() => {
    if (open) {
      setPage(1)
      setSearch('')
      setSearchDebounced('')
    }
  }, [open])

  const productList = useAxios<
    PaginationResponseInterface<IProduct>
  >(
    open
      ? {
        method: 'GET',
        url: '/product',
        params: {
          page,
          perPage,
          search: searchDebounced || undefined,
        },
      }
      : undefined,
  )

  const handleSearch = useCallback(() => {
    setSearchDebounced(search)
    setPage(1)
  }, [search])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleSelect = (product: IProduct) => {
    onSelect(product)
  }

  const records = productList.data?.records ?? []
  const total = productList.data?.count ?? 0

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">เลือกสินค้า</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', gap: 1, pt: 1 }}>
          <TextField
            label="ค้นหาชื่อสินค้า, รหัสสินค้า"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={handleSearch}>
            ค้นหา
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell width={80}>รูป</TableCell>
                <TableCell>ชื่อ</TableCell>
                <TableCell>รหัส</TableCell>
                <TableCell align="right">ราคาขาย</TableCell>
                <TableCell align="center" width={120}>
                  เลือก
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productList.isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    กำลังโหลด...
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    ไม่พบสินค้า
                  </TableCell>
                </TableRow>
              ) : (
                records.map((product) => {
                  const isSelected = selectedIds.has(product.objectId)
                  return (
                    <TableRow key={product.objectId} hover>
                      <TableCell>
                        <ImgTag
                          imageUrl={product.image}
                          name={product.name}
                          size={40}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{product.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {product.code}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          ฿{product.price?.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isSelected}
                          onClick={() => handleSelect(product)}
                        >
                          {isSelected ? 'เลือกแล้ว' : 'เลือก'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {total > 0 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              total={total}
              current={page}
              pageSize={perPage}
              showSizeChanger={false}
              onChange={(newPage) => setPage(newPage)}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
